/**
 * Attributes & groups — the REAL data for the admin screens (6 Oct 2026), replacing the design
 * prototype's sample store. One load per organisation (orgApi.attrsLoad → api/_lib/attrActions.ts)
 * gives every screen the same shape the prototype had: attributes, people with their values merged
 * in, groups and the Who-can-be lists. Each save goes to the server, then everything reloads — the
 * organisation's data is small, and one source of truth beats patching copies.
 *
 * Only owners and admins load this (the server refuses others). A member's own details come from
 * orgApi.attrsMine instead — see MyOrgDetails.
 */

import { useEffect, useSyncExternalStore } from 'react'
import { orgApi } from './orgApi'
import { useOrgs } from './useOrgs'
import { evaluate, type AttrDef, type Group, type Lookup, type Person, type PersonList, type Value } from './attributesModel'

export type { Group } from './attributesModel'

export interface AttrData {
  defs: AttrDef[]
  people: Person[]
  groups: Group[]
  eligible: Record<PersonList, string[]>
  csvColumns?: string[]
}

export const EMPTY_ATTRS: AttrData = { defs: [], people: [], groups: [], eligible: { manager: [], trainer: [], coach: [] } }

interface Snap { orgId: string | null; data: AttrData | null; error: string | null; loading: boolean; saveError: string | null }
let snap: Snap = { orgId: null, data: null, error: null, loading: false, saveError: null }
const listeners = new Set<() => void>()
const set = (p: Partial<Snap>) => { snap = { ...snap, ...p }; listeners.forEach(l => l()) }
let seq = 0

export async function reloadAttrs(orgId: string | null = snap.orgId): Promise<void> {
  if (!orgId) return
  const mine = ++seq
  set({ orgId, loading: true, ...(snap.orgId !== orgId ? { data: null, error: null } : {}) })
  try {
    const b = await orgApi.attrsLoad(orgId)
    if (mine !== seq) return
    set({ loading: false, error: null, data: { defs: b.defs, people: b.people, groups: b.groups, eligible: b.eligible, csvColumns: b.csvColumns ?? undefined } })
  } catch (e) {
    if (mine === seq) set({ loading: false, error: (e as Error).message || 'Could not load attributes' })
  }
}

/** For tests: forget everything loaded. */
export function resetAttrs() { seq++; snap = { orgId: null, data: null, error: null, loading: false, saveError: null }; listeners.forEach(l => l()) }

function useSnap(): Snap {
  const { current, isManager } = useOrgs()
  const s = useSyncExternalStore(cb => { listeners.add(cb); return () => listeners.delete(cb) }, () => snap, () => snap)
  const orgId = current?.id ?? null
  useEffect(() => {
    if (orgId && isManager && snap.orgId !== orgId) void reloadAttrs(orgId)
  }, [orgId, isManager])
  return s
}

/**
 * Reload when a page or panel opens (6 Oct 2026). The data is cached across pages, so without
 * this a member who joined after the first load was missing from Edit details — no name, no
 * values — while the Members list (loaded fresh) already showed them. Keeps what is shown
 * while it reloads, so nothing flickers.
 */
export function useFreshAttrs() {
  useEffect(() => { if (snap.orgId) void reloadAttrs() }, [])
}

/** The organisation's attribute data — EMPTY_ATTRS until it has loaded. */
export function useAttrData(): AttrData {
  const s = useSnap()
  return s.data ?? EMPTY_ATTRS
}

/** Whether it has loaded, and why not. */
export function useAttrStatus(): { ready: boolean; loading: boolean; error: string | null; saveError: string | null; clearSaveError: () => void; reload: () => Promise<void> } {
  const s = useSnap()
  return { ready: !!s.data, loading: s.loading, error: s.error, saveError: s.saveError, clearSaveError: () => set({ saveError: null }), reload: () => reloadAttrs() }
}

/**
 * Save, then reload. A refusal is kept as `saveError` (every attribute page shows it in a
 * banner) and thrown, so a form can stay open instead of closing as if it had saved.
 */
async function then<T>(p: Promise<T>): Promise<T> {
  set({ saveError: null })
  try { return await p }
  catch (e) { set({ saveError: (e as Error).message || 'That could not be saved' }); throw e }
  finally { await reloadAttrs() }
}
/**
 * Show a change at once, then save (7 Oct 2026). Adding someone to "Who can be a manager"
 * waited for the save AND a full reload before they appeared — about a second of nothing,
 * long enough to click again. A refusal reloads, which puts the list back, and shows the banner.
 */
function optimistic<T>(change: (d: AttrData) => AttrData, p: () => Promise<T>): Promise<T> {
  if (snap.data) set({ data: change(snap.data) })
  return then(p())
}
const org = () => {
  if (!snap.orgId) throw new Error('No organisation is open')
  return snap.orgId
}

export const attrs = {
  saveAttr: (def: AttrDef, deletedOptionIds: string[] = []) => then(orgApi.attrSave(org(), def, deletedOptionIds)),
  deleteAttr: (key: string) => then(orgApi.attrDelete(org(), key)),
  // Optimistic (14 Oct 2026 — the Attributes table edits cells in place): shown at once; a refusal
  // reloads, which puts the old value back.
  setValues: (userId: string, values: Record<string, Value>) =>
    optimistic(d => ({ ...d, people: d.people.map(p => (p.id === userId ? { ...p, values: { ...p.values, ...values } } : p)) }), () => orgApi.valuesSet(org(), userId, values)),
  addEligible: (list: PersonList, userIds: string[]) => {
    const id = org()
    return optimistic(d => ({ ...d, eligible: { ...d.eligible, [list]: [...d.eligible[list], ...userIds.filter(u => !d.eligible[list].includes(u))] } }),
      () => orgApi.eligibleSet(id, list, { add: userIds }))
  },
  removeEligible: (list: PersonList, userId: string, reassignTo: string | null = null) => {
    const id = org()
    return optimistic(d => ({ ...d, eligible: { ...d.eligible, [list]: d.eligible[list].filter(u => u !== userId) } }),
      () => orgApi.eligibleSet(id, list, { remove: { userId, reassignTo } }))
  },
  saveGroup: (g: Partial<Group> & { kind: Group['kind']; name: string }) => then(orgApi.groupSave(org(), g)),
  setCsvColumns: (keys: string[]) => then(orgApi.csvColumnsSet(org(), keys)),
}

// ─── Helpers the screens share ───────────────────────────────────────────────

export const nameOf = (s: Pick<AttrData, 'people'>, id: string) => s.people.find(p => p.id === id)?.name ?? 'Unknown'
/** A person for a picker: name, with their email under it (7 Oct 2026 — every people list shows emails). */
export const personItem = (s: Pick<AttrData, 'people'>, id: string) => {
  const p = s.people.find(x => x.id === id)
  return { id, label: p?.name ?? 'Unknown', hint: p?.email || undefined }
}

/** Option labels and person names for the plain-words rule. */
export function lookupFor(s: AttrData): Lookup {
  return {
    optionLabel: (attr, id) => {
      const o = s.defs.find(d => d.key === attr)?.options?.find(x => x.id === id)
      return { label: o?.label ?? id, archived: !!o?.archived }
    },
    personName: id => nameOf(s, id),
  }
}

/** A group's members right now — attribute group: worked out from the rule; user group: the active ones picked. */
export function membersOf(g: Group, s: Pick<AttrData, 'people'>): Person[] {
  return g.kind === 'auto' ? evaluate(s.people, g.rule) : s.people.filter(p => p.active && g.members.includes(p.id))
}
